<template>
  <v-container>
    <v-breadcrumbs
      class="px-0 py-2"
      :items="breadcrumbs"
    />
    <h2>
      売上詳細
    </h2>
    <v-card v-if="deal">
      <v-card-title></v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="2">
            <div class="overline">
              取引番号
            </div>
            <h4>{{ deal.deal_no }}&nbsp;</h4>
            <v-divider />
          </v-col>
          <v-col cols="12" md="2">
            <div class="overline">
              受注番号
            </div>
            <h4>{{ deal.order_no }}&nbsp;</h4>
            <v-divider />
          </v-col>
          <v-col cols="12" md="2">
            <div class="overline">
              依頼番号
            </div>
            <h4>{{ deal.request_no }}&nbsp;</h4>
            <v-divider />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <div class="overline">
              営業担当者
            </div>
            <h4>{{ deal.sales_staff }}&nbsp;</h4>
            <v-divider />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <h3>取引先</h3>
            <div class="ml-3 mt-1">
              <h4>{{ deal.supplier.name }}</h4>
              <p class="mb-0">
                <span v-if="deal.supplier.zip_code">〒&nbsp;{{ deal.supplier.zip_code }}</span>&emsp;{{ deal.supplier.address }}
              </p>
              <p class="mb-0">
                電話番号: {{ deal.supplier.phone_number || '-' }}
              </p>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <h3>取引先事業場</h3>
            <h4>{{ deal.location.name }}</h4>
            <p class="mb-0">
              <span v-if="deal.location.zip_code">〒&nbsp;{{ deal.location.zip_code }}</span>&emsp;{{ deal.location.address }}
            </p>
            <p class="mb-0">
              電話番号: {{ deal.location.phone_number || '-' }}
            </p>
          </v-col>
          <v-col cols="12" md="4">
            <h3>請求先</h3>
            <template v-if="deal.billing_address.name">
            <div class="ml-3 mt-1">
              <h4>{{ deal.billing_address.name }}</h4>
              <ul style="list-style: none">
                <li>
                  <span v-if="deal.billing_address.zip_code">〒&nbsp;{{ deal.billing_address.zip_code }}</span>&emsp;{{ deal.billing_address.address }}
                </li>
                <li>
                  電話番号: {{ deal.billing_address.phone_number || '-' }}
                </li>
                <li>
                  締め日:&nbsp;{{ deal.billing_address.close_day_type_name || '-' }}
                </li>
                <li>
                  相殺/別建区分:&nbsp;{{ deal.billing_address.counterbalance_type_name || '-' }}
                </li>
              </ul>
            </div>
            </template>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="2">
            <div class="overline">
              現掛区分
            </div>
            <h4>{{ deal.receivable_type | receivable_type_label }}&nbsp;</h4>
            <v-divider />
          </v-col>
          <v-col cols="12" md="2">
            <div class="overline">
              伝票発行
            </div>
            <h4>{{ deal.issue_voucher_name }}&nbsp;</h4>
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="2">
            <div class="overline">
              取引日
            </div>
            <h4>{{ deal.trading_date | date_format }}&nbsp;</h4>
            <v-divider />
          </v-col>
          <v-col cols="12" md="2">
            <div class="overline">
              売上計上日
            </div>
            <h4>{{ deal.sales_date | date_format }}&nbsp;</h4>
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-card flat>
              <v-card-text>
                <v-data-table
                  :headers="tableHeaders"
                  :items="deal.deal_details"
                  dense
                  disable-sort
                >
                  <template v-slot:[`item.trading_date`]="{item}">
                    {{ item.trading_date | date_format }}
                  </template>
                  <template v-slot:[`item.sales_date`]="{item}">
                    {{ item.sales_date | date_format }}
                  </template>
                  <template v-slot:[`item.sales_item_price`]="{item}">
                    {{ item.sales_item_price_code }}：{{ item.sales_item_price_name }}
                  </template>
                  <template v-slot:[`item.tax_type`]="{ item }">
                    {{ item.tax_type | tax_type }}
                  </template>
                  <template v-slot:[`item.quantity`]="{ item }">
                    {{ parseFloat(item.quantity) | comma_format }}
                  </template>
                  <template v-slot:[`item.unit_price`]="{ item }">
                    {{ parseFloat(item.unit_price) | comma_format }}
                  </template>
                  <template v-slot:[`item.detailed_amount`]="{ item }">
                    {{ parseFloat(item.detailed_amount) | comma_format }}
                  </template>
                  <!-- 明細備考 -->
                  <template v-slot:[`item.detailed_memo`]="{ item }">
                    <v-tooltip
                      v-if="item.detailed_memo"
                      top
                      :open-on-hover="false"
                    >
                      <template #activator="{ on }">
                        <v-btn
                          icon
                          tile
                          retain-focus-on-click
                          @click="on.click"
                          @blur="on.blur"
                        >
                          <v-icon>mdi-message-text-outline</v-icon>
                        </v-btn>
                      </template>
                      <div>
                        <small>{{ item.detailed_memo }}</small>
                      </div>
                    </v-tooltip>
                  </template>
                  <!-- /明細備考 -->
                  <template v-slot:[`item._destroy`]="{ item }">
                    <v-btn
                      class="info"
                      small
                      @click="showDetailDialogButtonClickEvent(item)"
                    >
                      詳細
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4" offset-md="8">
            <v-simple-table>
              <thead>
                <tr>
                  <th class="text-right">
                    小計金額（税抜）
                  </th>
                  <th class="text-right">
                    消費税金額
                  </th>
                  <th class="text-right">
                    非課税対象
                  </th>
                  <th class="text-right">
                    合計金額
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-right">
                    {{ deal.subtotal_excluding_tax_amount | comma_format }}
                  </td>
                  <td class="text-right">
                    {{ deal.sales_tax_amount | comma_format }}
                  </td>
                  <td class="text-right">
                    {{ deal.tax_exempt_amount | comma_format }}
                  </td>
                  <td class="text-right">
                    {{ deal.total_amount | comma_format }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="backButtonClickEvent"
        >
          戻る
        </v-btn>
      </v-card-actions>
    </v-card>
    <reference-dialog
      :enable-dialog="enableDetailDialog"
      :deal-detail="dealDetail"
      @closeCallback="closeDialogCallback"
    />
  </v-container>
</template>

<script>
import ReferenceDialog from './dialogs/_ReferenceDialog'
import { mapActions } from 'vuex'

export default {
  name: 'DriverSalesTransaction',
  components: {
    ReferenceDialog
  },
  mixins: [],
  props: {},
  data() {
    return {
      enableDetailDialog: false,
      breadcrumbs: [
        { text: '売上管理', disabled: false, to: '/driver/sales/transactions', exact: true },
        { text: '売上詳細', disabled: true, to: '' },
      ],
      deal: null,
      tableHeaders: [
        { text: '売上品目コード', value: 'sales_item_price' },
        { text: '取引数量', value: 'quantity', align: 'end' },
        { text: '取引単位', value: 'sales_item_unit_name' },
        { text: '単価', value: 'unit_price', align: 'end' },
        { text: '単位あたり', value: 'quantity_per_unit' },
        { text: '課税区分', value: 'tax_type' },
        { text: '明細金額', value: 'detailed_amount', align: 'end' },
        { text: '明細備考', value: 'detailed_memo', align: 'end' },
        { value: '_destroy', text: '編集', align: 'end' },
      ],
      dealDetail: null,
    }
  },
  computed: {},
  watch: {},
  created() {
    this.fetchDeal({id: this.$route.params.id}).then((content) => {
      this.deal = content
    })
  },
  methods: {
    ...mapActions('DriverSalesDeal', {
      fetchDeal: 'show',
    }),
    backButtonClickEvent () {
      this.$router.push( { name: 'DriverSalesTransactions' } )
    },
    showDetailDialogButtonClickEvent (item) {
      this.dealDetail = item
      this.enableDetailDialog = true
    },
    closeDialogCallback () {
      this.enableDetailDialog = false
    },
  }
}
</script>
